import React, { useState } from "react"
import { FeedSpinner } from "@components/shared/Spinner"
import { RequestError } from "@components/shared/RequestError"
import { useBandPosts } from "@hooks/useBandPosts"
import { RecentShowRow } from "./RecentShowRow"
import { ExternalEventForm, EventFormValues } from "./ExternalEventForm"
import { EmbedInstructionsModal } from "./EmbedInstructionsModal"
import { OutlineButton } from "@components/shared/Button"
import { downloadShowsCsv } from "../../requests/userBands"
import Pagination from "../../components/shared/Pagination"

interface RecentShowsProps {
  bandId: number
}

interface PostCollaborator {
  band_id: number
  status: string
}

interface Post {
  id: number
  band_id?: number
  post_type?: string
  active_date: string
  published_at?: string
  title: string
  nada_path?: string
  nada_url?: string
  ticket_type?: string
  interested_count?: number
  band_nada_tickets_sold?: number
  ticket_price?: number
  total_band_share?: number
  total_bn_share?: number
  total_promotion_share?: number
  post_collaborators?: PostCollaborator[]
  external_url?: string
  external_photo_src?: string
  content_hash?: { location?: string; ticket_price?: string }
}

function postToFormValues(post: Post): EventFormValues {
  return {
    url: post.external_url ?? "",
    title: post.title ?? "",
    location: post.content_hash?.location ?? "",
    date: post.active_date ?? post.published_at ?? "",
    image: post.external_photo_src ?? "",
    ticket_price: post.content_hash?.ticket_price ?? "",
  }
}

const TableHeader: React.FC = () => (
  <li className="grid grid-cols-12 lg:grid-cols-24 items-center font-bold uppercase border-b-2 border-black text-xs leading:none">
    <div className="col-span-10">Event</div>
    <div className="hidden lg:block lg:col-span-2">Ticket Price</div>
    <div className="hidden lg:block lg:col-span-2">Ticket Type</div>
    <div className="hidden lg:block lg:col-span-2">RSVPs</div>
    <div className="hidden lg:block lg:col-span-2">Purchases</div>
    <div className="hidden lg:block lg:col-span-2">Total Sales</div>
    <div className="hidden lg:block lg:col-span-2">Donations</div>
    <div className="col-span-2">Actions</div>
  </li>
)

export const RecentShows: React.FC<RecentShowsProps> = ({ bandId }) => {
  const [upcomingPage, setUpcomingPage] = useState(1)
  const [pastPage, setPastPage] = useState(1)
  const [externalEventModalOpen, setExternalEventModalOpen] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [embedModalOpen, setEmbedModalOpen] = useState(false)

  const {
    data: upcomingData,
    status: upcomingStatus,
    error: upcomingError,
    isPreviousData: upcomingIsPrev,
  } = useBandPosts(bandId, "future_concerts", true, upcomingPage, 20, true)

  const {
    data: pastData,
    status: pastStatus,
    error: pastError,
    isPreviousData: pastIsPrev,
  } = useBandPosts(bandId, "past_concerts", true, pastPage, 10, true)

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-4">
        <button
          type="button"
          onClick={() => setEmbedModalOpen(true)}
          className="text-sm text-gray-600 hover:text-gray-900 underline focus:outline-none"
        >
          Embed your bandNada concert feed in your own website
        </button>
        <div className="flex items-center gap-2">
          <OutlineButton
            type="button"
            onClick={() => setExternalEventModalOpen(true)}
            className="text-gray-800 text-sm"
          >
            + Add External Event
          </OutlineButton>
          <OutlineButton
            type="button"
            disabled={isExportingCsv}
            onClick={async () => {
              setIsExportingCsv(true)
              try {
                await downloadShowsCsv(bandId)
              } finally {
                setIsExportingCsv(false)
              }
            }}
            className="text-gray-800 text-sm"
          >
            {isExportingCsv ? "Exporting..." : "Export to CSV"}
          </OutlineButton>
        </div>
      </div>

      {/* Upcoming Shows */}
      <h2 className="font-bold text-xl uppercase border-b-4 border-black mb-6">
        Upcoming Shows
      </h2>
      {upcomingStatus === "loading" ? (
        <FeedSpinner />
      ) : upcomingStatus === "error" ? (
        <RequestError error={upcomingError} />
      ) : upcomingData ? (
        upcomingData.posts.length === 0 ? (
          <div className="text-sm text-gray-600 my-3">No upcoming shows.</div>
        ) : (
          <>
            <ul
              className={`lg:text-base text-xs ${upcomingIsPrev ? "opacity-50" : ""}`}
            >
              <TableHeader />
              {upcomingData.posts.map((post: Post) => (
                <RecentShowRow
                  key={post.id}
                  post={post}
                  currentBandId={bandId}
                  onEdit={setEditingPost}
                />
              ))}
            </ul>
            <Pagination
              page={upcomingData.page}
              totalPages={upcomingData.total_pages}
              totalCount={upcomingData.total_count}
              hasNextPage={upcomingData.has_next_page}
              onPrev={() => setUpcomingPage((p) => Math.max(1, p - 1))}
              onNext={() => setUpcomingPage((p) => p + 1)}
            />
          </>
        )
      ) : null}

      {/* Past Shows */}
      <h2 className="font-bold text-xl uppercase border-b-4 border-black mt-10 mb-4">
        Past Shows
      </h2>
      {pastStatus === "loading" ? (
        <FeedSpinner />
      ) : pastStatus === "error" ? (
        <RequestError error={pastError} />
      ) : pastData ? (
        pastData.posts.length === 0 ? (
          <div className="text-sm text-gray-600 my-3">No past shows.</div>
        ) : (
          <>
            <ul
              className={`lg:text-base text-xs ${pastIsPrev ? "opacity-50" : ""}`}
            >
              <TableHeader />
              {pastData.posts.map((post: Post) => (
                <RecentShowRow
                  key={post.id}
                  post={post}
                  currentBandId={bandId}
                  onEdit={setEditingPost}
                />
              ))}
            </ul>
            <Pagination
              page={pastData.page}
              totalPages={pastData.total_pages}
              totalCount={pastData.total_count}
              hasNextPage={pastData.has_next_page}
              onPrev={() => setPastPage((p) => Math.max(1, p - 1))}
              onNext={() => setPastPage((p) => p + 1)}
            />
          </>
        )
      ) : null}

      {(externalEventModalOpen || editingPost != null) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="external-event-form-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden relative">
            <button
              type="button"
              onClick={() => {
                setExternalEventModalOpen(false)
                setEditingPost(null)
              }}
              className="absolute top-3 right-3 z-10 p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              <ExternalEventForm
                bandId={bandId}
                onClose={() => {
                  setExternalEventModalOpen(false)
                  setEditingPost(null)
                }}
                postId={editingPost?.id}
                initialValues={
                  editingPost ? postToFormValues(editingPost) : undefined
                }
              />
            </div>
          </div>
        </div>
      )}

      {embedModalOpen && (
        <EmbedInstructionsModal
          bandIdentifier={String(bandId)}
          onClose={() => setEmbedModalOpen(false)}
        />
      )}
    </div>
  )
}
